import React, { useState, useEffect, useRef } from 'react';
import type { Chef, ChefOriginLanguage, Language } from '../types';
import XIcon from './icons/XIcon';
import ChefAvatar from './ChefAvatar';
import { getTranslation } from '../utils/translations';
import { finalizeChefDraft, normalizeChefPayload, buildChefSaveUpdate } from '../utils/chefCsv';
import { getChefWebsites } from '../utils/chefDisplay';
import { resolveChefInstagramUsername } from '../utils/chefMerge';
import { chefNameLooksLikeRawInstagramFullName, parseInstagramFullName, profileFromInstagramOwner } from '../utils/instagramChefProfile';
import { fetchInstagramProfile } from '../services/geminiService';
import { ingestChefProfilePicUrl, resolveChefImageUrlForSave } from '../utils/chefMedia';
import { isStoredMediaUrl } from '../services/mediaService';
import CopyButton from './CopyButton';

interface ChefEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    chef: Chef | null;
    isCreate?: boolean;
    linkedUserEdit?: boolean;
    language?: Language;
    onSave: (id: string, updates: Partial<Chef>) => Promise<void>;
    onCreate?: (chef: Chef) => Promise<void>;
}

const ChefEditModal: React.FC<ChefEditModalProps> = ({
    isOpen,
    onClose,
    chef,
    isCreate = false,
    linkedUserEdit = false,
    language = 'en',
    onSave,
    onCreate,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [draft, setDraft] = useState<Partial<Chef>>({});
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const draftInitKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isOpen || !chef) {
            draftInitKeyRef.current = null;
            return;
        }

        const initKey = isCreate ? '__create__' : chef.id;
        if (draftInitKeyRef.current === initKey) return;
        draftInitKeyRef.current = initKey;

        const websites = getChefWebsites(chef);
        let name = chef.name?.trim() || chef.englishName || '';
        let description = chef.description?.trim() || '';
        if (chefNameLooksLikeRawInstagramFullName(name, name)) {
            const parsed = parseInstagramFullName(name, chef.englishName);
            name = parsed.name || name;
            description = description || parsed.description || '';
        }
        const initialImageUrl = chef.imageUrl || '';
        setDraft({
            ...chef,
            name,
            description,
            imageUrl: isStoredMediaUrl(initialImageUrl) ? '' : initialImageUrl,
            website: undefined,
            instagramUsername: resolveChefInstagramUsername(chef) || chef.instagramUsername || '',
            websites: websites.length ? websites : [''],
        });
        setError('');
    }, [isOpen, chef, isCreate]);

    const websites = draft.websites ?? [''];

    const updateWebsite = (index: number, value: string) => {
        setDraft((prev) => {
            const next = [...(prev.websites ?? [''])];
            next[index] = value;
            return { ...prev, websites: next };
        });
    };

    const addWebsite = () => {
        setDraft((prev) => ({ ...prev, websites: [...(prev.websites ?? []), ''] }));
    };

    const removeWebsite = (index: number) => {
        setDraft((prev) => {
            const next = (prev.websites ?? []).filter((_, i) => i !== index);
            return { ...prev, websites: next.length ? next : [''] };
        });
    };

    if (!isOpen || !chef) return null;

    const handleRefreshInstagram = async () => {
        const username = draft.instagramUsername?.trim();
        const userId = draft.instagramUserId?.trim();
        if (!username && !userId) {
            setError(t('chefInstagramUsernameRequired'));
            return;
        }
        setRefreshing(true);
        setError('');
        try {
            const profile = await fetchInstagramProfile({
                ...(username ? { username } : {}),
                ...(userId ? { userId } : {}),
            });
            if (!profile) {
                setError(t('chefInstagramRefreshFailed'));
                return;
            }
            const resolvedUsername = profile.username?.trim() || username || '';
            const parsed = profileFromInstagramOwner(profile.fullName, resolvedUsername);
            const hasWebsites = (draft.websites ?? []).some((link) => link.trim());
            const storedImageUrl = profile.profilePicUrl
                ? await ingestChefProfilePicUrl(chef.id, profile.profilePicUrl, draft.imageUrl)
                : draft.imageUrl;
            setDraft((prev) => ({
                ...prev,
                instagramUsername: resolvedUsername || prev.instagramUsername,
                instagramUserId: profile.userId || userId || prev.instagramUserId,
                name: parsed.name || prev.name,
                description: prev.description?.trim()
                    || profile.biography?.trim()
                    || parsed.description
                    || prev.description,
                imageUrl: storedImageUrl || prev.imageUrl,
                websites: hasWebsites ? prev.websites : (profile.websites?.length ? profile.websites : prev.websites),
            }));
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : t('chefInstagramRefreshFailed'));
        } finally {
            setRefreshing(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = normalizeChefPayload({
                ...draft,
                name: draft.name?.trim() || draft.englishName?.trim() || '',
                englishName: draft.englishName?.trim() || draft.name?.trim() || '',
                originCountry: draft.originCountry,
                websites: draft.websites,
                description: draft.description,
                instagramUsername: draft.instagramUsername,
                instagramUserId: draft.instagramUserId,
                imageUrl: draft.imageUrl,
            });

            if (isCreate) {
                let created = finalizeChefDraft(payload);
                if (!created) {
                    setError(t('chefRequestNameRequired'));
                    return;
                }
                if (created.imageUrl) {
                    const storedUrl = await resolveChefImageUrlForSave(created.id, created.imageUrl);
                    if (storedUrl) {
                        created = { ...created, imageUrl: storedUrl };
                    }
                }
                if (!onCreate) return;
                await onCreate(created);
            } else {
                const storedUrl = await resolveChefImageUrlForSave(chef.id, draft.imageUrl, chef.imageUrl);
                if (linkedUserEdit) {
                    const updates: Partial<Chef> = {
                        name: payload.name,
                        englishName: payload.englishName,
                        description: payload.description?.trim() || '',
                        websites: payload.websites,
                        updatedAt: new Date().toISOString(),
                    };
                    if (storedUrl) {
                        updates.imageUrl = storedUrl;
                    }
                    await onSave(chef.id, updates);
                } else {
                    const saveDraft = storedUrl ? { ...draft, imageUrl: storedUrl } : draft;
                    const updates = buildChefSaveUpdate(saveDraft) as Partial<Chef>;
                    if (storedUrl) {
                        updates.imageUrl = storedUrl;
                    }
                    await onSave(chef.id, updates);
                }
            }
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[650] flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2rem] shadow-2xl overflow-hidden border dark:border-slate-800 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center shrink-0">
                    <h2 className="text-xl font-black text-slate-800 dark:text-slate-100">
                        {isCreate ? t('newChef') : linkedUserEdit ? t('editChefProfile') : t('editChef')}
                    </h2>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-full">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-xl text-sm font-bold">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-center py-2">
                        <ChefAvatar
                            imageUrl={draft.imageUrl || chef.imageUrl}
                            name={draft.englishName || chef.englishName}
                            size="xl"
                            className="border-4 border-white dark:border-slate-800 shadow-md"
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefName')}</label>
                        <input
                            value={draft.name || ''}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefEnglishName')}</label>
                        <input
                            value={draft.englishName || ''}
                            onChange={(e) => setDraft({ ...draft, englishName: e.target.value })}
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefDescription')}</label>
                        <textarea
                            value={draft.description || ''}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            placeholder={t('chefDescriptionPlaceholder')}
                            rows={3}
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm resize-none"
                        />
                    </div>
                    {!linkedUserEdit && (
                    <>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefInstagramUsername')}</label>
                        <div className="flex gap-2">
                            <input
                                value={draft.instagramUsername || ''}
                                onChange={(e) => setDraft({ ...draft, instagramUsername: e.target.value.replace(/^@/, '') })}
                                placeholder="username"
                                className="flex-1 p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                            />
                            <button
                                type="button"
                                onClick={() => void handleRefreshInstagram()}
                                disabled={refreshing || (!draft.instagramUsername?.trim() && !draft.instagramUserId?.trim())}
                                className="px-4 py-3 bg-brand-sage/10 text-brand-sage font-black rounded-xl text-[10px] uppercase tracking-widest disabled:opacity-50 shrink-0"
                            >
                                {refreshing ? '...' : t('refreshFromInstagram')}
                            </button>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">{t('chefInstagramUsernameHint')}</p>
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefInstagramUserId')}</label>
                        <div className="flex gap-2">
                            <input
                                value={draft.instagramUserId || ''}
                                onChange={(e) => setDraft({ ...draft, instagramUserId: e.target.value.replace(/\D/g, '') })}
                                placeholder="43119366"
                                inputMode="numeric"
                                className="flex-1 p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm font-mono"
                            />
                            <CopyButton
                                text={draft.instagramUserId || ''}
                                copyLabel={t('copy')}
                                copiedLabel={t('copied')}
                                disabled={!draft.instagramUserId?.trim()}
                                className="self-center"
                            />
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">{t('chefInstagramUserIdHint')}</p>
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('region')}</label>
                        <input
                            value={draft.originCountry || ''}
                            onChange={(e) => setDraft({ ...draft, originCountry: e.target.value })}
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                        />
                    </div>
                    </>
                    )}
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefLinks')}</label>
                        <p className="text-[10px] text-slate-400 mb-2">{t('chefLinksHint')}</p>
                        <div className="space-y-2">
                            {websites.map((link, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <input
                                        value={link}
                                        onChange={(e) => updateWebsite(index, e.target.value)}
                                        placeholder={t('chefLinkPlaceholder')}
                                        className="flex-1 p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                                    />
                                    {websites.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeWebsite(index)}
                                            className="p-2 text-slate-400 hover:text-red-500 rounded-full shrink-0"
                                            aria-label={t('removeLink')}
                                        >
                                            <XIcon className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={addWebsite}
                            className="mt-2 text-xs font-black text-brand-sage uppercase tracking-widest hover:underline"
                        >
                            + {t('addLink')}
                        </button>
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('chefImageUrl')}</label>
                        <input
                            value={draft.imageUrl || ''}
                            onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
                            placeholder="https://"
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                        />
                    </div>
                    {!linkedUserEdit && (
                    <>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('originLanguage')}</label>
                        <select
                            value={draft.originLanguage || 'en'}
                            onChange={(e) => setDraft({ ...draft, originLanguage: e.target.value as ChefOriginLanguage })}
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                        >
                            <option value="he">{t('hebrew')}</option>
                            <option value="en">{t('english')}</option>
                        </select>
                    </div>

                    {!isCreate && (
                        <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!draft.disabled}
                                onChange={(e) => setDraft({ ...draft, disabled: e.target.checked })}
                                className="w-4 h-4 rounded border-slate-300 text-brand-sage focus:ring-brand-sage"
                            />
                            <div>
                                <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{t('disableChef')}</span>
                                <p className="text-[10px] text-slate-400 mt-0.5">{t('disableChefHint')}</p>
                            </div>
                        </label>
                    )}
                    </>
                    )}
                </div>

                <div className="p-6 border-t dark:border-slate-800 flex gap-3 shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-xl text-xs uppercase tracking-widest"
                    >
                        {t('cancel')}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 py-3 bg-brand-sage text-white font-black rounded-xl text-xs uppercase tracking-widest disabled:opacity-50"
                    >
                        {saving ? '...' : t('save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChefEditModal;
